/**
 * 工程表の2回目の小さな部品（#101、docs/39 §4.2、Figma A・B）
 *   StartCards … 空の工程表の4つのカード（見積から（おすすめ）・雛形から・前の案件からうつす・白紙から）
 *   DelayBand  … 遅れの知らせ（操作の列の下の1行。#105 で工程ごとの帯をやめた）
 * 色だけで伝えない：遅れは「⚠」と文、バーに「n日遅れ」の文字
 */
import { successorsOf, type ScheduleLink, type ScheduleRow } from "@genbaryoku/contract";
import { SCHEDULE_MSG } from "./msg.js";
import { t } from "../../labels.js";
import { useEffect, useRef, useState } from "react";
import { CrIcon } from "../../components/Create.jsx";

type Card = { k: "estimate" | "template" | "group" | "blank"; title: string; sub: string; icon: "wand" | "layers" | "copy" | "plus"; disabled?: boolean; badge?: string };

export function StartCards({
  estimates,
  noProject = false,
  templates,
  sources,
  onPick,
}: {
  /** この案件の見積の枚数（失注は数えない） */
  estimates: number;
  /** 案件なしの{schedule}（#291）：「見積から」は押せず「案件に紐づけると見積から作成できます」 */
  noProject?: boolean;
  /** 雛形の数・工程表のあるほかの案件の数（読んでいる間は undefined） */
  templates: number | undefined;
  sources: number | undefined;
  onPick: (k: Card["k"]) => void;
}) {
  const cards: Card[] = [
    {
      k: "estimate",
      title: SCHEDULE_MSG.fromEstimate,
      sub: noProject ? SCHEDULE_MSG.fromEstimateNeedsProject : estimates ? `見積 ${estimates}枚` : SCHEDULE_MSG.noEstimate,
      icon: "wand",
      disabled: noProject || !estimates,
      badge: SCHEDULE_MSG.fromEstimateRecommended,
    },
    { k: "template", title: SCHEDULE_MSG.fromTemplate, sub: templates == null ? "" : templates ? `テンプレート ${templates}件` : SCHEDULE_MSG.noTemplate, icon: "layers", disabled: templates === 0 },
    { k: "group", title: SCHEDULE_MSG.fromGroup, sub: sources == null ? "" : sources ? t(`{schedule}のある{project} ${sources}件`) : SCHEDULE_MSG.noSource, icon: "copy", disabled: sources === 0 },
    { k: "blank", title: SCHEDULE_MSG.blank, sub: "1行ずつ追加", icon: "plus" },
  ];
  return (
    <div className="sch-starts" role="group" aria-label={SCHEDULE_MSG.startTitle} data-testid="sch-starts">
      {cards.map((c) => (
        <button key={c.k} type="button" className={"sch-start" + (c.k === "estimate" && !c.disabled ? " rec" : "")} disabled={c.disabled} onClick={() => onPick(c.k)} data-testid={`sch-start-${c.k}`}>
          <span className="ic" aria-hidden="true">
            <CrIcon k={c.icon} />
          </span>
          <span className="tt">
            <b>{c.title}</b>
            {c.badge && !c.disabled ? <span className="chip ai">{c.badge}</span> : null}
          </span>
          <span className="sub">{c.sub}</span>
        </button>
      ))}
    </div>
  );
}

/**
 * 遅れの知らせ（#105 の3＝1行・40px。枠線なし・注意の地・朱の文字は「遅れ n工程」だけ）。
 *   1工程：「{名前}が n日遅れています」＋「このまま」「n日延ばす」
 *   2工程以上：「遅れ n工程　{名前} n日・{名前} n日」＋「このまま」（全部）「延ばす…」（工程ごとに 名前・n日遅れ・「n日延ばす」の小さな一覧）
 */
export function DelayBand({
  delayed,
  links,
  onKeep,
  onExtend,
  readOnly = false,
}: {
  delayed: { row: ScheduleRow; delay: number }[];
  links: readonly ScheduleLink[];
  onKeep: (ids: string[]) => void;
  onExtend: (id: string) => void;
  /** 見るだけ（タブレットのスイッチが切。#102）：知らせるだけで「このまま」「延ばす」は出さない */
  readOnly?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        ref.current?.querySelector<HTMLButtonElement>("[data-testid=sch-delay-more]")?.focus();
      }
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  useEffect(() => {
    if (delayed.length < 2) setOpen(false);
  }, [delayed.length]);
  if (!delayed.length) return null;
  const one = delayed.length === 1 ? delayed[0]! : null;
  const after = one ? successorsOf([...links], one.row.id).size - 1 : 0;
  const shown = delayed.slice(0, 3);
  return (
    <div className="sch-delays" role="region" aria-label="遅れの知らせ" data-testid="sch-delays" ref={ref}>
      <CrIcon k="warn" />
      {one ? (
        <span className="tx">
          <b className="shu" data-testid="sch-delay" data-row-id={one.row.id}>
            {SCHEDULE_MSG.delayNotice(one.row.name, one.delay)}
          </b>
          {after > 0 ? <span className="sub">{t(`後続の ${after}{task}も延びます`)}</span> : null}
        </span>
      ) : (
        <span className="tx">
          <b className="shu" data-testid="sch-delay-count">{t(`遅れ ${delayed.length}{task}`)}</b>
          <span className="names">
            {shown.map(({ row, delay }, i) => (
              <span key={row.id} data-testid="sch-delay" data-row-id={row.id}>
                {i ? "・" : ""}
                {row.name || "（名前なし）"} {delay}日
              </span>
            ))}
            {delayed.length > 3 ? <span>・{SCHEDULE_MSG.delayMore(delayed.length - 3)}</span> : null}
          </span>
        </span>
      )}
      <span className="sp" />
      {readOnly ? null : (
        <>
      <button type="button" className="btn text" onClick={() => onKeep(delayed.map((x) => x.row.id))} data-testid="sch-delay-keep">
        {SCHEDULE_MSG.delayKeep}
      </button>
      {one ? (
        <button type="button" className="btn tonal" onClick={() => onExtend(one.row.id)} data-testid="sch-delay-extend">
          {SCHEDULE_MSG.delayExtend(one.delay)}
        </button>
      ) : (
        <span className="sch-delaymenu">
          <button type="button" className="btn tonal" aria-expanded={open} onClick={() => setOpen((o) => !o)} data-testid="sch-delay-more">
            延長…
          </button>
          {open ? (
            <div className="sch-delaypop" role="group" aria-label={t("{task}ごとに延長")} data-testid="sch-delay-pop">
              {delayed.map(({ row, delay }) => (
                <div key={row.id} className="it" data-testid="sch-delay-item" data-row-id={row.id}>
                  <span className="nm">{row.name || "（名前なし）"}</span>
                  <span className="pill shu">{SCHEDULE_MSG.delayBadge(delay)}</span>
                  <button type="button" className="btn text" onClick={() => onExtend(row.id)} data-testid="sch-delay-extend">
                    {SCHEDULE_MSG.delayExtend(delay)}
                  </button>
                </div>
              ))}
            </div>
          ) : null}
        </span>
      )}
        </>
      )}
    </div>
  );
}

/**
 * 着手の遅れの知らせ（#292、docs/102 §4。％ OFF の会社だけ）：開始日を過ぎて未着手の{task}。終わりの遅れ（DelayBand）と分けて、朱でなく墨・「このまま」だけ。
 *   1{task}：「{名前}の着手が n日遅れています」／2{task}以上：「着手の遅れ n{task}　{名前} n日・…」
 */
export function StartDelayBand({ delayed, onKeep, readOnly = false }: { delayed: { row: ScheduleRow; delay: number }[]; onKeep: (ids: string[]) => void; readOnly?: boolean }) {
  if (!delayed.length) return null;
  const one = delayed.length === 1 ? delayed[0]! : null;
  const shown = delayed.slice(0, 3);
  return (
    <div className="sch-delays sumi" role="region" aria-label="着手の遅れの知らせ" data-testid="sch-start-delays">
      <CrIcon k="clock" />
      {one ? (
        <span className="tx">
          <b data-testid="sch-start-delay" data-row-id={one.row.id}>
            {SCHEDULE_MSG.startDelayNotice(one.row.name, one.delay)}
          </b>
        </span>
      ) : (
        <span className="tx">
          <b data-testid="sch-start-delay-count">{SCHEDULE_MSG.startDelayCount(delayed.length)}</b>
          <span className="names">
            {shown.map(({ row, delay }, i) => (
              <span key={row.id} data-testid="sch-start-delay" data-row-id={row.id}>
                {i ? "・" : ""}
                {row.name || "（名前なし）"} {delay}日
              </span>
            ))}
            {delayed.length > 3 ? <span>・{SCHEDULE_MSG.delayMore(delayed.length - 3)}</span> : null}
          </span>
        </span>
      )}
      <span className="sp" />
      {readOnly ? null : (
        <button type="button" className="btn text" onClick={() => onKeep(delayed.map((x) => x.row.id))} data-testid="sch-start-delay-keep">
          {SCHEDULE_MSG.delayKeep}
        </button>
      )}
    </div>
  );
}
